import React, { useState, useEffect } from 'react';
import { Button, Grid2, TextField, Typography, Select, MenuItem, FormControl, CircularProgress, InputLabel, Alert, Card, FormControlLabel, Checkbox, FormGroup, Snackbar } from '@mui/material';
import { authServices } from '../../services/authentication/authServices';
import { useNavigate, useLocation } from 'react-router-dom';
import { availableRoles } from '../../constants/roles';
import { useAuth } from '../../services/authentication/authContext';
import { useTranslation } from 'react-i18next';
import { userServices } from '../../services/userServices';
import { teamServices } from '../../services/teamServices';
import { assistanceTeamServices } from '../../services/assistanceTeamServices';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import '../../css/Form.css';
import PageHeader from '../ui/PageHeader';
import ConfirmationDialog from '../common/ConfirmationDialog';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const UserForm = () => {
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [joinDate, setJoinDate] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [emailId, setEmailId] = useState('');
  const [address, setAddress] = useState('');
  const [designation, setDesignation] = useState('');
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState([]);
  const [active, setActive] = useState(true);
  const [targetHours, setTargetHours] = useState('');
  const [error, setError] = useState(null);
  const [errorEmail, setErrorEmail] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const { accessToken } = useAuth();
  const { t, i18n } = useTranslation();
  const [userEmail, setUserEmail] = useState('');
  const [id, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.userData;
  const navigationUrl = "/home/users";
  const [teams, setTeams] = useState([]);
  const [showTeamList, setShowTeamList] = useState(false);
  const [teamIds, setTeamIds] = useState('');
  const [assistanceTeamId, setAssistanceTeamId] = useState('');
  const [assistanceTeamsList, setAssistanceTeamsList] = useState([]);
  const [loadingAssistanceTeams, setLoadingAssistanceTeams] = useState(false);
  const [ownerIds, setOwnerIds] = useState([]);
  const [editUserId, setEditUserId] = useState(null);
  const isAdminOrSubAdmin = roles.includes("SUPER_ADMIN") || roles.includes("ADMIN") || roles.includes("SUB_ADMIN");
  const [isDirty, setIsDirty] = useState(false);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  const canUserHaveTeam = (userRoles = roles, uId = editUserId || id, currentOwnerIds = ownerIds) => {
    const isOwner = Array.isArray(currentOwnerIds) && currentOwnerIds.length > 0 && uId && currentOwnerIds.includes(uId);
    if (isOwner) return false;
    const isAdmin = Array.isArray(userRoles) && userRoles.some((r) => ["SUPER_ADMIN", "ADMIN", "SUB_ADMIN"].includes(r));
    if (isAdmin) return false;
    return true;
  };

  useEffect(() => {
    if (user) {
      setUserEmail(user.emailId);
      setEditUserId(user.id);
      fetchOwnerUserList();
    }
  }, [user]);

  useEffect(() => {
    if (ownerIds.length > 0 && editUserId) {
      setShowTeamList(canUserHaveTeam(roles, editUserId, ownerIds));
    }
  }, [ownerIds, editUserId, roles]);

  const fetchOwnerUserList = () => {
    teamServices.getAllOwnerIdsList()
      .then((response) => {
        setOwnerIds(response || []);
      })
      .catch((error) => {
        console.error("Error fetching owner Ids List:", error);
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      return dateString;
    } catch {
      return '';
    }
  };

  const fetchAssistanceTeamsForParent = async (pTeamId) => {
    if (!pTeamId) {
      setAssistanceTeamsList([]);
      return;
    }
    setLoadingAssistanceTeams(true);
    try {
      const response = await assistanceTeamServices.getAssistanceTeamsByParentTeam(pTeamId);
      setAssistanceTeamsList(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching assistance teams for parent team:", err);
      setAssistanceTeamsList([]);
    } finally {
      setLoadingAssistanceTeams(false);
    }
  };

  const handleTeamChange = (newTeamId) => {
    setTeamIds(newTeamId);
    setAssistanceTeamId('');
    if (newTeamId) {
      fetchAssistanceTeamsForParent(newTeamId);
    } else {
      setAssistanceTeamsList([]);
    }
    if (!loading) {
      setIsDirty(true);
    }
  };

  useEffect(() => {
    const fetchTeams = async () => {
      setLoading(true);
      try {
        const teamsData = await teamServices.getAllTeamsListForUserScreen();

        if (Array.isArray(teamsData)) {
          setTeams(teamsData);
        } else {
          console.error('Expected an array of teams, but received:', teamsData);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, []);


  useEffect(() => {
    if (accessToken) {
      if (userEmail) {
        userServices.getUserDetailsWithoutStore(userEmail)
          .then((response) => {
            setUserId(response.id || '');
            setFirstName(response.firstName || '');
            setMiddleName(response.middleName || '');
            setLastName(response.lastName || '');
            setBirthDate(formatDate(response?.birthDate) || '');
            setJoinDate(formatDate(response?.joinDate) || '');
            setMobileNo(response.mobileNo || '');
            setEmailId(response.emailId || '');
            setAddress(response.address || '');
            setDesignation(response.designation || '');
            setTitle(response.title || '');
            setDepartment(response.department || '');
            setPassword(response.password || '');
            setRoles(response.roles || []);
            setTeamIds(response.teamId || '');
            setAssistanceTeamId(response.assistanceTeamId || '');
            if (response.teamId) {
              fetchAssistanceTeamsForParent(response.teamId);
            } else {
              setAssistanceTeamsList([]);
            }
            setTargetHours(response.targetHours != null ? response.targetHours : '');
            setActive(response.active !== undefined ? response.active : true);
            setLoading(false);

            setShowTeamList(canUserHaveTeam(response.roles || [], response.id, ownerIds));

          })
          .catch((error) => {
            setError();
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  }, [userEmail, accessToken]);

  const handleRoleChange = (event) => {
    const selectedRoles = event.target.checked
      ? [...roles, event.target.value]
      : roles.filter((role) => role !== event.target.value);

    if (selectedRoles.length === 0) {
      setError("Please select at least one role.");
      setTimeout(() => {
        setError(null);
      }, 4000);
    } else {
      setError(null);
    }

    const showTeams = canUserHaveTeam(selectedRoles, editUserId || id, ownerIds);
    setShowTeamList(showTeams);

    setRoles(selectedRoles);
    if (!loading) {
      setIsDirty(true);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (roles.length === 0) {
      setError("Please select at least one role.");
      setTimeout(() => {
        setError(null);
      }, 4000);
      return;
    }

    const shouldHaveTeam = canUserHaveTeam(roles, editUserId || id, ownerIds);
    const teamId = shouldHaveTeam ? (teamIds || null) : null;
    const selectedAssistanceTeamId = (shouldHaveTeam && teamIds) ? (assistanceTeamId || null) : null;

    // Perform form submission or validation here
    const formData = {
      firstName,
      middleName,
      lastName,
      birthDate,
      joinDate,
      mobileNo,
      emailId,
      address,
      designation,
      title,
      department,
      password,
      roles,
      teamId,
      assistanceTeamId: selectedAssistanceTeamId,
      targetHours,
      active: active ? true : false
    };

    const updateFormData = {
      id,
      firstName,
      middleName,
      lastName,
      birthDate,
      joinDate,
      mobileNo,
      emailId,
      address,
      designation,
      title,
      department,
      password,
      roles,
      teamId,
      assistanceTeamId: selectedAssistanceTeamId,
      targetHours,
      active: active ? true : false
    };

    if (accessToken) {
      if (userEmail) {
        userServices.updateUserData(updateFormData)
          .then((updatedUser) => {
            localStorage.setItem("successMessage", "User Updated successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      } else {
        authServices.postRegisterData(formData)
          .then((newUser) => {
            localStorage.setItem("successMessage", "User added successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      }

    }
  };

  const handleCancel = () => {
    handleBack();
  };

  const handleBack = () => {
    if (isDirty) {
      setShowBackConfirmation(true);
    } else {
      navigate(navigationUrl);
    }
  };

  const handleConfirmBack = () => {
    setShowBackConfirmation(false);
    navigate(navigationUrl);
  };

  return (
    <div className="main-form-container">
      <PageHeader 
        title={userEmail ? "Edit User" : "Add User"} 
        left={
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        }
      />

      {errorEmail && (
        <Alert variant="outlined" severity="error" className="error-alert-user">
          {errorEmail}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="form" onChange={() => { if (!loading) setIsDirty(true); }}>
        <Grid2 container spacing={3}>

          {/* First Name */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="First Name"
              variant="outlined"
              fullWidth
              value={firstName ?? ''}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter First Name"
              required
            />
          </Grid2>

          {/* Middle Name */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Middle Name"
              variant="outlined"
              fullWidth
              value={middleName ?? ''}
              onChange={(e) => setMiddleName(e.target.value)}
              placeholder="Enter Middle Name"
            />
          </Grid2>

          {/* Last Name */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Last Name"
              variant="outlined"
              fullWidth
              value={lastName ?? ''}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter Last Name"
              required
            />
          </Grid2>

          {/* Date of Birth */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Date of Birth"
              variant="outlined"
              fullWidth
              type="date"
              value={birthDate ?? ''}
              onChange={(e) => setBirthDate(e.target.value)}
              required
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid2>

          {/* Mobile No */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Mobile No."
              variant="outlined"
              fullWidth
              type='number'
              value={mobileNo ?? ''}
              onChange={(e) => setMobileNo(e.target.value)}
              placeholder="Enter Mobile Number"
              required
            />
          </Grid2>

          {/* Email ID */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            {userEmail ? (
              <TextField
                label="Email ID"
                variant="outlined"
                fullWidth
                value={emailId ?? ''}
                onChange={(e) => setEmailId(e.target.value)}
                type="email"
                placeholder="Enter Email ID"
                disabled
              />
            ) : (
              <TextField
                label="Email ID"
                variant="outlined"
                fullWidth
                value={emailId ?? ''}
                onChange={(e) => setEmailId(e.target.value)}
                type="email"
                placeholder="Enter Email ID"
                required
                error={!!errorEmail}
                helperText={errorEmail && errorEmail.includes("Email") ? errorEmail : ""}
              />
            )}
          </Grid2>

          {/* Address */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Address"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={address ?? ''}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter Address"
              required
            />
          </Grid2>

          {/* Designation */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Designation</InputLabel>
              <Select
                value={designation ?? ''}
                onChange={(e) => setDesignation(e.target.value)}
                label="Designation"
                required
              >
                <MenuItem value="Jr. Account Executive">Jr. Account Executive</MenuItem>
                <MenuItem value="Account Executive">Account Executive</MenuItem>
                <MenuItem value="Senior Account Executive">Senior Account Executive</MenuItem>
                <MenuItem value="Accountant">Accountant</MenuItem>
                <MenuItem value="Senior Accountant">Senior Accountant</MenuItem>
                <MenuItem value="Assistant Manager">Assistant Manager</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="Senior Manager">Senior Manager</MenuItem>
                <MenuItem value="Director">Director</MenuItem>
              </Select>
            </FormControl>
          </Grid2>

          {/* Title */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Title</InputLabel>
              <Select
                value={title ?? ''}
                onChange={(e) => setTitle(e.target.value)}
                label="Title"
                required
              >
                <MenuItem value="Mr.">Mr.</MenuItem>
                <MenuItem value="Mrs.">Mrs.</MenuItem>
                <MenuItem value="Miss.">Miss.</MenuItem>
                <MenuItem value="Ms.">Ms.</MenuItem>
              </Select>
            </FormControl>
          </Grid2>

          {/* Department */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={department ?? ''}
                onChange={(e) => setDepartment(e.target.value)}
                label="Department"
                required
              >
                <MenuItem value="Initial Review">Initial Review</MenuItem>
                <MenuItem value="Accounting">Accounting</MenuItem>
                <MenuItem value="Bookkeeping">Bookkeeping</MenuItem>
                <MenuItem value="Payroll">Payroll</MenuItem>
                <MenuItem value="Audit">Audit</MenuItem>
                <MenuItem value="Marketing">Marketing</MenuItem>
                <MenuItem value="IT">IT</MenuItem>
              </Select>
            </FormControl>
          </Grid2>

          {/* Date of Join */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Date of Join"
              variant="outlined"
              fullWidth
              type="date"
              value={joinDate ?? ''}
              onChange={(e) => setJoinDate(e.target.value)}
              required
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Target Hours"
              variant="outlined"
              fullWidth
              type="number"
              value={targetHours ?? ''}
              onChange={(e) => setTargetHours(e.target.value)}
              placeholder="Enter Target Hours"
              required={!isAdminOrSubAdmin}
              slotProps={{
                input: {
                  min: 0,
                  max: 400,
                },
              }}
            />
          </Grid2>

          {/* Password Field */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            {userEmail ? (
              <TextField
                label="Password"
                variant="outlined"
                fullWidth
                type="password"
                value={password ?? ''}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter Password"
                disabled
              />
            ) : (
              <TextField
                label="Password"
                variant="outlined"
                fullWidth
                type="password"
                value={password ?? ''}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter Password"
                required
              />
            )}
          </Grid2>

          {/* Permissions */}
          <Grid2 size={{ xs: 12 }}>
            <Typography variant="h6" className="centered-typography">
              Manage Permissions
            </Typography>

            {/* Display error message */}
            {error && (
              <Alert variant="outlined" severity="error" className="error-alert-user">
                {error}
              </Alert>
            )}

            <Card className="permission-card">
              <FormControl component="fieldset" fullWidth className="form-control">
                <FormGroup row sx={{ justifyContent: 'space-evenly', gap: 2 }}>
                  {Object.keys(availableRoles)
                    .filter((roleKey) => roleKey !== "SUPER_ADMIN")
                    .map((roleKey) => (
                      <Grid2 size={{ xs: 6, sm: 4, md: 3 }} key={roleKey}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              value={roleKey}
                              checked={roles.includes(roleKey)}
                              onChange={handleRoleChange}
                              sx={{
                                '&.Mui-checked': {
                                  color: '#045864',
                                },
                              }}
                            />
                          }
                          label={availableRoles[roleKey]}
                          className="checkbox-label"
                        />
                      </Grid2>
                    ))}
                </FormGroup>
              </FormControl>
            </Card>
          </Grid2>

          {showTeamList && (
            <>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Team Name</InputLabel>
                  <Select
                    value={(Array.isArray(teams) && teams.some((t) => String(t.id) === String(teamIds))) ? teamIds : ''}
                    onChange={(e) => handleTeamChange(e.target.value)}
                    label="Team Name"
                    disabled={loading}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {loading ? (
                      <MenuItem value="" disabled>
                        <CircularProgress size={24} />
                      </MenuItem>
                    ) : (
                      Array.isArray(teams) && teams.length > 0 ? (
                        teams.map((team) => (
                          <MenuItem key={team.id} value={team.id}>
                            {team.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>No teams available</MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid2>

              {teamIds && assistanceTeamsList.length > 0 && (
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Assistance Team</InputLabel>
                    <Select
                      value={(Array.isArray(assistanceTeamsList) && assistanceTeamsList.some((at) => String(at.id) === String(assistanceTeamId))) ? assistanceTeamId : ''}
                      onChange={(e) => {
                        setAssistanceTeamId(e.target.value);
                        if (!loading) setIsDirty(true);
                      }}
                      label="Assistance Team"
                      disabled={loading || loadingAssistanceTeams}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {assistanceTeamsList.map((at) => (
                        <MenuItem key={at.id} value={at.id}>
                          {at.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid2>
              )}
            </>
          )}

          {/* Active / Inactive Checkbox */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Card className="permission-card">
              <FormControl component="fieldset" fullWidth className="form-control">
                <FormGroup row sx={{ justifyContent: 'flex-start', pl: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!active}
                        onChange={(e) => setActive(!e.target.checked)}
                        sx={{
                          "&.Mui-checked": {
                            color: "#045864",
                          },
                        }}
                      />
                    }
                    label="Inactive User"
                    className="checkbox-label"
                  />
                </FormGroup>
              </FormControl>
            </Card>
          </Grid2>

          {/* Submit Button */}
          <Grid2 size={{ xs: 12 }} className="form-actions-user" sx={{ flexDirection: { xs: 'row !important', sm: 'row' }, gap: { xs: 2, sm: 0 }, '& > button': { flex: { xs: 1, sm: 'none' }, margin: { xs: '0 !important', sm: '' } } }}>
            {userEmail ?
              (<Button type="submit" variant="contained" color="primary" className="form-button-user"
                startIcon={<EditIcon />}>
                {t('user.update')}
              </Button>
              ) : (
                <Button type="submit" variant="contained" color="primary" className="form-button-user"
                  startIcon={<SaveIcon />}>
                  {t('user.save')}
                </Button>
              )}
            <Button type="button" variant="contained" color="inherit" onClick={handleCancel} className="form-button-user-reset"
              startIcon={<CancelIcon />}>
              {t('user.cancel')}
            </Button>
          </Grid2>

        </Grid2>
      </form>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ whiteSpace: 'pre-line' }}>
          {Array.isArray(snackbarMessage) ? (
            snackbarMessage.map((msg, index) => (
              <div key={index}>{msg}</div>
            ))
          ) : (
            snackbarMessage
          )}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={showBackConfirmation}
        onClose={() => setShowBackConfirmation(false)}
        onConfirm={handleConfirmBack}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmLabel="Leave Page"
        confirmColor="error"
      />
    </div>
  );
};

export default UserForm;
